'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

interface BandwidthRecord {
  id: string;
  device_id: string | null;
  reparto_id: string | null;
  vlan: string;
  fascia_oraria: string;
  rx_mbps: number;
  tx_mbps: number;
  rx_bytes_totali: number;
  tx_bytes_totali: number;
  timestamp_rilevazione: string;
}

interface Device {
  id: string;
  hostname: string;
}

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export default function BandwidthContent() {
  const [records, setRecords] = useState<BandwidthRecord[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [vlanFilter, setVlanFilter] = useState('all');
  const [vlans, setVlans] = useState<string[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ device_id: '', vlan: '', rx_mbps: '', tx_mbps: '', fascia_oraria: 'mattina' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bwRes, devRes] = await Promise.all([
        supabase.from('bandwidth_usage').select('*').order('timestamp_rilevazione', { ascending: true }).limit(200),
        supabase.from('devices').select('id, hostname'),
      ]);
      if (bwRes.error) throw bwRes.error;
      setRecords(bwRes.data || []);
      setDevices(devRes.data || []);
      const uniqueVlans = [...new Set((bwRes.data || []).map((r: BandwidthRecord) => r.vlan).filter(Boolean))];
      setVlans(uniqueVlans);
    } catch (err: any) {
      toast.error('Errore caricamento banda: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = records.filter(r => {
    const matchDevice = deviceFilter === 'all' || r.device_id === deviceFilter;
    const matchVlan = vlanFilter === 'all' || r.vlan === vlanFilter;
    return matchDevice && matchVlan;
  });

  const chartData = filtered.map(r => ({
    time: new Date(r.timestamp_rilevazione).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
    rx: parseFloat(r.rx_mbps.toFixed(1)),
    tx: parseFloat(r.tx_mbps.toFixed(1)),
    fascia: r.fascia_oraria,
  }));

  const vlanData = vlans.map(vlan => {
    const vlanRecords = records.filter(r => r.vlan === vlan);
    const avgRx = vlanRecords.length ? vlanRecords.reduce((s, r) => s + r.rx_mbps, 0) / vlanRecords.length : 0;
    const avgTx = vlanRecords.length ? vlanRecords.reduce((s, r) => s + r.tx_mbps, 0) / vlanRecords.length : 0;
    return { vlan, rx: parseFloat(avgRx.toFixed(1)), tx: parseFloat(avgTx.toFixed(1)) };
  });

  const totalRx = filtered.reduce((s, r) => s + r.rx_bytes_totali, 0);
  const totalTx = filtered.reduce((s, r) => s + r.tx_bytes_totali, 0);
  const formatBytes = (b: number) => b > 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`;

  const handleAddRecord = async () => {
    const rx = parseFloat(form.rx_mbps);
    const tx = parseFloat(form.tx_mbps);
    if (isNaN(rx) || rx < 0) { toast.error('RX Mbps deve essere >= 0'); return; }
    if (isNaN(tx) || tx < 0) { toast.error('TX Mbps deve essere >= 0'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('bandwidth_usage').insert({
        device_id: form.device_id || null,
        vlan: form.vlan || null,
        rx_mbps: rx,
        tx_mbps: tx,
        fascia_oraria: form.fascia_oraria,
        rx_bytes_totali: rx * 1024 * 1024,
        tx_bytes_totali: tx * 1024 * 1024,
        timestamp_rilevazione: new Date().toISOString(),
      });
      if (error) throw error;
      toast.success('Rilevazione aggiunta');
      setShowAddForm(false);
      setForm({ device_id: '', vlan: '', rx_mbps: '', tx_mbps: '', fascia_oraria: 'mattina' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    if (filtered.length === 0) { toast.error('Nessun dato da esportare'); return; }
    const headers = ['Timestamp', 'Dispositivo', 'VLAN', 'Fascia', 'RX (Mbps)', 'TX (Mbps)', 'RX Totale', 'TX Totale'];
    const rows = filtered.map(r => [
      new Date(r.timestamp_rilevazione).toLocaleString('it-IT'),
      devices.find(d => d.id === r.device_id)?.hostname || '—',
      r.vlan || '—',
      r.fascia_oraria || '—',
      r.rx_mbps.toFixed(1),
      r.tx_mbps.toFixed(1),
      formatBytes(r.rx_bytes_totali),
      formatBytes(r.tx_bytes_totali),
    ]);
    const csv = '\uFEFF' + [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bandwidth_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full bg-background overflow-auto scrollbar-thin">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Utilizzo Banda</h1>
            <p className="text-sm text-secondary-color mt-0.5">Monitoraggio traffico per dispositivo, VLAN e fascia oraria</p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-surface border border-border text-secondary-color rounded-lg text-sm font-semibold hover:text-white hover:bg-surface-elevated transition-all">
              <Icon name="ArrowDownTrayIcon" size={15} />
              Esporta CSV
            </button>
            <button onClick={() => setShowAddForm(true)} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg text-sm font-semibold hover:bg-sky-500/20 transition-all">
              <Icon name="PlusIcon" size={15} />
              Aggiungi Rilevazione
            </button>
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg text-sm font-semibold hover:bg-sky-500/20 transition-all">
              <Icon name="ArrowPathIcon" size={15} />
              Aggiorna
            </button>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-4 gap-4 px-6 py-4">
        {[
          { label: 'RX Totale', value: formatBytes(totalRx), icon: 'ArrowDownTrayIcon', color: 'text-green-400' },
          { label: 'TX Totale', value: formatBytes(totalTx), icon: 'ArrowUpTrayIcon', color: 'text-sky-400' },
          { label: 'Picco RX', value: `${Math.max(...filtered.map(r => r.rx_mbps), 0).toFixed(1)} Mbps`, icon: 'ChartBarIcon', color: 'text-amber-400' },
          { label: 'Picco TX', value: `${Math.max(...filtered.map(r => r.tx_mbps), 0).toFixed(1)} Mbps`, icon: 'ChartBarIcon', color: 'text-violet-400' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={kpi.icon as any} size={16} className={kpi.color} />
              <span className="text-xs font-bold text-muted-color uppercase tracking-wider">{kpi.label}</span>
            </div>
            <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="px-6 pb-4 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <select value={deviceFilter} onChange={e => setDeviceFilter(e.target.value)} className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none appearance-none pr-7 cursor-pointer">
            <option value="all">Tutti i dispositivi</option>
            {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <div className="relative">
          <select value={vlanFilter} onChange={e => setVlanFilter(e.target.value)} className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none appearance-none pr-7 cursor-pointer">
            <option value="all">Tutte le VLAN</option>
            {vlans.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="px-6 pb-6 space-y-6">
          {/* Trend chart */}
          <div className="bg-surface border border-border rounded-xl p-5">
            <h2 className="text-base font-bold text-white mb-4">Andamento Traffico nel Tempo</h2>
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-color font-semibold">Nessun dato disponibile</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="rxGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="txGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} unit=" Mbps" />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} labelStyle={{ color: '#94a3b8' }} />
                  <Legend />
                  <Area type="monotone" dataKey="rx" name="RX (Mbps)" stroke="#22c55e" fill="url(#rxGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="tx" name="TX (Mbps)" stroke="#38bdf8" fill="url(#txGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* VLAN chart */}
          <div className="bg-surface border border-border rounded-xl p-5">
            <h2 className="text-base font-bold text-white mb-4">Utilizzo Medio per VLAN</h2>
            {vlanData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-color font-semibold">Nessun dato VLAN</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={vlanData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="vlan" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} unit=" Mbps" />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="rx" name="RX (Mbps)" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="tx" name="TX (Mbps)" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="text-base font-bold text-white">Dettaglio Rilevazioni</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-border">
                  <tr>
                    {['Timestamp', 'VLAN', 'Fascia Oraria', 'RX Mbps', 'TX Mbps', 'RX Totale', 'TX Totale'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.slice(-20).reverse().map(r => (
                    <tr key={r.id} className="hover:bg-surface-elevated/30 transition-colors">
                      <td className="px-4 py-3 text-secondary-color text-xs">{new Date(r.timestamp_rilevazione).toLocaleString('it-IT')}</td>
                      <td className="px-4 py-3 font-mono text-sky-400 text-xs">{r.vlan || '—'}</td>
                      <td className="px-4 py-3 text-secondary-color text-xs">{r.fascia_oraria || '—'}</td>
                      <td className="px-4 py-3 font-mono text-green-400">{r.rx_mbps.toFixed(1)}</td>
                      <td className="px-4 py-3 font-mono text-sky-400">{r.tx_mbps.toFixed(1)}</td>
                      <td className="px-4 py-3 text-secondary-color text-xs">{formatBytes(r.rx_bytes_totali)}</td>
                      <td className="px-4 py-3 text-secondary-color text-xs">{formatBytes(r.tx_bytes_totali)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Record Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white">Aggiungi Rilevazione Banda</h3>
              <button onClick={() => setShowAddForm(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Dispositivo</label>
                  <select value={form.device_id} onChange={e => setForm(p => ({ ...p, device_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                    <option value="">— Nessuno —</option>
                    {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">VLAN</label>
                  <input value={form.vlan} onChange={e => setForm(p => ({ ...p, vlan: e.target.value }))} placeholder="es. VLAN10" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">RX (Mbps)</label>
                  <input type="number" min="0" step="0.1" value={form.rx_mbps} onChange={e => setForm(p => ({ ...p, rx_mbps: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">TX (Mbps)</label>
                  <input type="number" min="0" step="0.1" value={form.tx_mbps} onChange={e => setForm(p => ({ ...p, tx_mbps: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Fascia Oraria</label>
                <select value={form.fascia_oraria} onChange={e => setForm(p => ({ ...p, fascia_oraria: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                  <option value="mattina">Mattina</option>
                  <option value="pomeriggio">Pomeriggio</option>
                  <option value="sera">Sera</option>
                  <option value="notte">Notte</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <button onClick={() => setShowAddForm(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
              <button onClick={handleAddRecord} disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Aggiungi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
